import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { SearchX } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { EmptyState } from '@/components/domain/EmptyState';
import { ErrorState } from '@/components/domain/ErrorState';
import { useT } from '@/i18n/useT';

export type RouterContext = { queryClient: QueryClient };

// Devtools chargés à la demande en développement seulement (jamais en test ni en production).
const showDevtools = import.meta.env.DEV && import.meta.env.MODE !== 'test';
const RouterDevtools = showDevtools
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : null;
const QueryDevtools = showDevtools
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
    )
  : null;

function RootLayout() {
  return (
    <>
      <Outlet />
      {RouterDevtools && QueryDevtools && (
        <Suspense fallback={null}>
          <RouterDevtools position="bottom-right" />
          <QueryDevtools buttonPosition="bottom-left" />
        </Suspense>
      )}
    </>
  );
}

function NotFound() {
  const t = useT();
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <EmptyState
        icon={SearchX}
        title={t('notFound.title')}
        description={t('notFound.description')}
        action={{ label: t('notFound.backHome'), to: { to: '/' } }}
      />
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: ({ error, reset }) => (
    <div className="flex min-h-svh items-center justify-center p-6">
      <ErrorState error={error} onRetry={reset} />
    </div>
  ),
});
